import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Computer as ComputerIcon,
  Security as SecurityIcon,
  Assessment as AssessmentIcon,
  Support as SupportIcon,
  Public as PublicIcon,
  Build as BuildIcon,
  BugReport as BugReportIcon,
  CloudQueue as CloudQueueIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const OITPortalSystemDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/pi-planning');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to PI Planning Board"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: '#003366', fontWeight: 'bold' }}>
            OIT Enterprise Service Portal
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            IT Service Management and Cybersecurity Operations Platform
          </Typography>
        </Box>
      </Box>

      {/* System Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            System Overview
          </Typography>
          <Typography variant="body1" paragraph>
            The OIT Enterprise Service Portal is CBP's centralized platform for IT service management, cybersecurity 
            operations, and infrastructure monitoring under the Chief Information Officer's purview. Built on ServiceNow, 
            the portal provides a unified interface for IT service delivery, incident management, change control, asset 
            management, and security operations, supporting 60,000+ CBP employees and contractors with enterprise-grade 
            IT services and cybersecurity protection.
          </Typography>
        </CardContent>
      </Card>

      {/* 5Ws Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        System Overview: The 5Ws
      </Typography>

      <Card sx={{ mb: 4 }}>
        <TableContainer>
          <Table aria-label="OIT Portal System 5Ws Overview">
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ width: '15%', bgcolor: '#D32F2F', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="What is OIT Portal"
                >
                  What
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    An <strong>enterprise IT service management (ITSM) platform</strong> built on ServiceNow that provides 
                    unified service delivery, cybersecurity operations, infrastructure monitoring, and IT support for all CBP 
                    personnel. Includes <strong>service catalog, incident/problem/change management, asset tracking, security 
                    operations center (SOC) tools</strong>, and IT governance capabilities.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#1976D2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Who uses OIT Portal"
                >
                  Who
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Operated by <strong>CBP Office of Information and Technology (OIT)</strong> under the Chief Information 
                    Officer. Primary users include <strong>60,000+ CBP employees and contractors</strong> for IT service 
                    requests, <strong>500+ IT support staff</strong> for service delivery, <strong>cybersecurity teams</strong> 
                    for security operations, and <strong>IT leadership</strong> for governance and reporting.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#2E7D32', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="When was OIT Portal deployed"
                >
                  When
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    Initial ServiceNow deployment in <strong>2015</strong> for basic ITSM. Major expansion in <strong>2018-2019</strong> 
                    with security operations integration. <strong>Continuous enhancement</strong> with new modules for asset 
                    management (2020), vulnerability management (2021), and <strong>AI-powered automation (2023-present)</strong>. 
                    Operates <strong>24/7/365</strong> with 99.9% availability.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#F57C00', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Where is OIT Portal deployed"
                >
                  Where
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    <strong>Cloud-hosted ServiceNow instance</strong> in FedRAMP High authorized environment. Accessible to 
                    <strong> all CBP personnel nationwide</strong> via web browser and mobile app. Integrated with <strong>CBP 
                    network infrastructure, Active Directory, and enterprise systems</strong>. Service desk teams located at 
                    <strong> CBP headquarters and regional support centers</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ bgcolor: '#7B1FA2', color: 'white', fontWeight: 'bold', fontSize: '1.1rem' }}
                  aria-label="Why OIT Portal exists"
                >
                  Why
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    To provide <strong>centralized, standardized IT service delivery</strong> across CBP's geographically 
                    distributed workforce. Ensures <strong>consistent service quality, rapid incident resolution, and proactive 
                    problem management</strong>. Enables <strong>cybersecurity operations, compliance tracking, and IT governance</strong> 
                    required for federal IT management. Critical for maintaining <strong>operational continuity and security posture</strong>.
                  </Typography>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Core Service Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <SupportIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Service Capabilities
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SupportIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  IT Service Management
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Comprehensive ITIL-based service delivery and support operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Service Catalog:</strong> 200+ IT services with automated provisioning</Typography></li>
                <li><Typography variant="body2"><strong>Incident Management:</strong> Multi-tier support with SLA tracking</Typography></li>
                <li><Typography variant="body2"><strong>Problem Management:</strong> Root cause analysis and prevention</Typography></li>
                <li><Typography variant="body2"><strong>Change Management:</strong> CAB approval workflow and scheduling</Typography></li>
                <li><Typography variant="body2"><strong>Request Fulfillment:</strong> Self-service portal and automation</Typography></li>
                <li><Typography variant="body2"><strong>Knowledge Management:</strong> Searchable KB with 5,000+ articles</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#D32F2F', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                  Cybersecurity Operations
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Integrated security operations and compliance management tools.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Security Incident Response:</strong> SIEM integration and threat tracking</Typography></li>
                <li><Typography variant="body2"><strong>Vulnerability Management:</strong> Scan results and remediation workflow</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Tracking:</strong> FISMA, NIST 800-53, FedRAMP controls</Typography></li>
                <li><Typography variant="body2"><strong>Risk Management:</strong> Risk register and mitigation planning</Typography></li>
                <li><Typography variant="body2"><strong>Security Assessments:</strong> ATO tracking and continuous monitoring</Typography></li>
                <li><Typography variant="body2"><strong>Threat Intelligence:</strong> Integration with DHS CISA feeds</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BuildIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Infrastructure Monitoring
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Real-time monitoring and management of IT infrastructure.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>System Health Monitoring:</strong> 10,000+ devices and applications</Typography></li>
                <li><Typography variant="body2"><strong>Performance Metrics:</strong> Real-time dashboards and alerting</Typography></li>
                <li><Typography variant="body2"><strong>Capacity Planning:</strong> Resource utilization and forecasting</Typography></li>
                <li><Typography variant="body2"><strong>Event Management:</strong> Automated correlation and escalation</Typography></li>
                <li><Typography variant="body2"><strong>Configuration Management:</strong> CMDB with 50,000+ CIs</Typography></li>
                <li><Typography variant="body2"><strong>Asset Management:</strong> Hardware/software inventory tracking</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BugReportIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Incident Response Coordination
                </Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Coordinated response to IT and security incidents across CBP.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Major Incident Management:</strong> War room coordination and communication</Typography></li>
                <li><Typography variant="body2"><strong>Crisis Communication:</strong> Stakeholder notifications and updates</Typography></li>
                <li><Typography variant="body2"><strong>Post-Incident Review:</strong> Lessons learned and improvement plans</Typography></li>
                <li><Typography variant="body2"><strong>Disaster Recovery:</strong> DR plan execution and testing</Typography></li>
                <li><Typography variant="body2"><strong>Business Continuity:</strong> Service restoration prioritization</Typography></li>
                <li><Typography variant="body2"><strong>Vendor Coordination:</strong> Third-party support escalation</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ServiceNow Platform Modules */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <ComputerIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        ServiceNow Platform Modules
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#003366' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Module</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Capability</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Users</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>IT Service Management (ITSM)</strong></TableCell>
              <TableCell>Incident, Problem, Change, Request Management</TableCell>
              <TableCell>60,000+ end users, 500+ IT staff</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>IT Operations Management (ITOM)</strong></TableCell>
              <TableCell>Discovery, Event, Orchestration, Cloud Management</TableCell>
              <TableCell>200+ infrastructure engineers</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Security Operations (SecOps)</strong></TableCell>
              <TableCell>Vulnerability Response, Threat Intelligence, Incident Response</TableCell>
              <TableCell>150+ cybersecurity analysts</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>IT Business Management (ITBM)</strong></TableCell>
              <TableCell>Project Portfolio, Demand, Resource Management</TableCell>
              <TableCell>100+ IT managers and PMO</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Governance, Risk & Compliance (GRC)</strong></TableCell>
              <TableCell>Risk Management, Policy Compliance, Audit Management</TableCell>
              <TableCell>75+ compliance officers</TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>HR Service Delivery (HRSD)</strong></TableCell>
              <TableCell>Employee Onboarding, Case Management, Knowledge</TableCell>
              <TableCell>60,000+ employees</TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>Customer Service Management (CSM)</strong></TableCell>
              <TableCell>External Stakeholder Support, Case Tracking</TableCell>
              <TableCell>50+ customer service reps</TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* System Scale */}
      <Typography variant="h5" gutterBottom sx={{ color: '#003366', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <PublicIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Platform Scale & Performance
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                60K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Active Users
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                CBP employees/contractors
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                500K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Annual Tickets
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Incidents and requests
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                50K+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Configuration Items
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                CMDB assets tracked
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #D32F2F' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#D32F2F', fontWeight: 'bold' }}>
                99.9%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Platform Availability
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                24/7/365 uptime
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Key Benefits */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Operational Benefits
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Centralized Service Delivery:</strong> Single portal for all IT services</Typography></li>
                <li><Typography variant="body2"><strong>Faster Resolution:</strong> Automated routing and SLA management</Typography></li>
                <li><Typography variant="body2"><strong>Improved Security:</strong> Integrated vulnerability and incident response</Typography></li>
                <li><Typography variant="body2"><strong>Better Visibility:</strong> Real-time dashboards and reporting</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Cost Reduction:</strong> Self-service and automation reduce support costs</Typography></li>
                <li><Typography variant="body2"><strong>Compliance:</strong> Built-in GRC for federal requirements</Typography></li>
                <li><Typography variant="body2"><strong>Knowledge Retention:</strong> Centralized KB reduces repeat issues</Typography></li>
                <li><Typography variant="body2"><strong>User Satisfaction:</strong> Consistent service experience across CBP</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Integration Ecosystem */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Integration Ecosystem
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Chip label="Active Directory" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Identity management</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Splunk SIEM" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Security event correlation</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Tenable Nessus" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Vulnerability scanning</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="SolarWinds" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Infrastructure monitoring</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Microsoft 365" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Email and collaboration</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="AWS GovCloud" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Cloud infrastructure</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Jira" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Development tracking</Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Chip label="Slack/Teams" color="primary" sx={{ mb: 1, width: '100%' }} />
              <Typography variant="caption" display="block">Collaboration platforms</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default OITPortalSystemDetail;
